/**
 * useTmdbBatch -- Batch-resolve TMDB canonical artwork for a collection of items.
 *
 * Architecture:
 * - sessionStorage-backed persistent cache (survives page refresh within session)
 * - Module-level Map mirrors storage for instant access (no JSON parse per read)
 * - Incremental: only fetches items not already in cache
 * - Fire-and-forget fetches: no AbortController, runs to completion
 * - Chunks requests to stay within the 60-item batch endpoint limit
 * - Deduplicates in-flight requests to prevent double-fetching
 *
 * Items without a tmdbId are absent from the map — callers fall back to
 * Emby/Jellyfin thumbnail proxy URLs.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { useCoverArtContext } from "../contexts/CoverArtSettingsContext";
import type { TmdbImageData } from "./useTmdbImages";
import { buildTmdbProxyUrl } from "./useTmdbImages";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface BatchItem {
  id: string;
  tmdbId: string | null | undefined;
  type: string;
}

type BatchPayloadItem = { tmdbId: string; mediaType: "movie" | "tv"; itemId: string };

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const CHUNK_SIZE = 60;
const STORAGE_KEY = "wsd-tmdb-cache";

// ─────────────────────────────────────────────────────────────────────────────
// Module-level persistent cache + sessionStorage backing
// ─────────────────────────────────────────────────────────────────────────────

/** In-memory mirror for instant access. Populated from sessionStorage on load. */
const tmdbCache = new Map<string, TmdbImageData>();

/** Items currently being fetched — prevents duplicate in-flight requests. */
const inFlightIds = new Set<string>();

/** Listener callbacks to notify hook instances when cache updates. */
const listeners = new Set<() => void>();

/** Write-through: updates both in-memory and sessionStorage. */
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function persistToStorage(): void {
  try {
    const obj: Record<string, TmdbImageData> = {};
    for (const [k, v] of tmdbCache) {
      obj[k] = v;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch { /* storage full or unavailable */ }
}

function schedulePersist(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    persistToStorage();
  }, 500);
}

function notifyListeners(): void {
  for (const cb of listeners) cb();
}

/** Hydrate in-memory cache from sessionStorage on module load. */
function hydrateFromStorage(): void {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, TmdbImageData>;
    for (const [k, v] of Object.entries(obj)) {
      if (v) {
        tmdbCache.set(k, v);
      }
    }
  } catch { /* corrupt or unavailable */ }
}

hydrateFromStorage();

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function toTmdbMediaType(type: string): "movie" | "tv" | null {
  if (type === "Movie") return "movie";
  if (type === "Series" || type === "Episode") return "tv";
  return null;
}

export function getTmdbPosterUrl(
  map: Map<string, TmdbImageData>,
  itemId: string,
  maxWidth = 400,
): string | null {
  const entry = map.get(itemId);
  if (!entry?.bestPoster) return null;
  return buildTmdbProxyUrl(entry.bestPoster.filePath, "poster", maxWidth);
}

export function getTmdbBackdropUrl(
  map: Map<string, TmdbImageData>,
  itemId: string,
  maxWidth = 1920,
): string | null {
  const entry = map.get(itemId);
  if (!entry?.bestBackdrop) return null;
  return buildTmdbProxyUrl(entry.bestBackdrop.filePath, "backdrop", maxWidth);
}

/** Fire a single batch request and write results into the module cache. */
async function fetchAndCacheChunk(
  chunk: BatchPayloadItem[],
): Promise<void> {
  try {
    const res = await fetch("/api/streaming/tmdb/images/batch", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify({ items: chunk }),
    });
    const json = (await res.json()) as { ok?: boolean; data?: { results: Record<string, TmdbImageData> } };
    if (json.ok && json.data?.results) {
      for (const [itemId, data] of Object.entries(json.data.results)) {
        // Cache ALL returned entries — including negatives (both null).
        // This prevents infinite retry loops for items that have no art.
        tmdbCache.set(itemId, data);
      }
      schedulePersist();
    }
  } catch {
    // Network error — items will be retried on next render cycle
  } finally {
    for (const item of chunk) {
      inFlightIds.delete(item.itemId);
    }
  }
}

/** Collect uncached items and fire batch fetch. Fire-and-forget. */
function fetchNeeded(items: BatchItem[]): void {
  const needed: BatchPayloadItem[] = [];
  for (const item of items) {
    if (!item.tmdbId) continue;
    if (tmdbCache.has(item.id)) continue;
    if (inFlightIds.has(item.id)) continue;
    const mediaType = toTmdbMediaType(item.type);
    if (!mediaType) continue;
    needed.push({ tmdbId: item.tmdbId, mediaType, itemId: item.id });
  }
  if (needed.length === 0) return;

  // Mark as in-flight before any async work
  for (const item of needed) {
    inFlightIds.add(item.itemId);
  }

  // Chunk into groups of CHUNK_SIZE
  const chunks: BatchPayloadItem[][] = [];
  for (let i = 0; i < needed.length; i += CHUNK_SIZE) {
    chunks.push(needed.slice(i, i + CHUNK_SIZE));
  }

  // Fire-and-forget: allSettled ensures we always notify even if some chunks fail
  void Promise.allSettled(chunks.map((chunk) => fetchAndCacheChunk(chunk)))
    .then(() => notifyListeners());
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useTmdbBatch(
  items: BatchItem[],
): Map<string, TmdbImageData> {
  const { tmdbEnabled } = useCoverArtContext();

  // Tick counter — incremented when cache updates, forces useMemo re-derivation
  const [tick, setTick] = useState(0);

  // Stable ref to latest items for use in non-cleanup callbacks
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Subscribe to cache updates
  useEffect(() => {
    const cb = () => setTick((t) => t + 1);
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);

  // Fetch uncached items whenever items change or cache updates (tick)
  // No AbortController — fetches run to completion even across re-renders
  // Gated: skip all fetches when TMDB is disabled
  useEffect(() => {
    if (tmdbEnabled) fetchNeeded(items);
  }, [items, tick, tmdbEnabled]);

  // Build result map from cache — re-derives when items change OR cache updates (tick)
  // Returns empty map when TMDB is disabled (components fall back to native artwork)
  return useMemo(() => {
    if (!tmdbEnabled) return new Map<string, TmdbImageData>();
    const map = new Map<string, TmdbImageData>();
    for (const item of items) {
      const cached = tmdbCache.get(item.id);
      if (cached) map.set(item.id, cached);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tick, tmdbEnabled]);
}
